import { getAuthToken } from './auth/tokenStorage';
import axios from 'axios';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';
import { redactError } from '../utils/redactError';
import {
  LOYALTY_RECORD_FIELDS,
  openCredentialFields,
  sealCredentialFields,
} from './encryption/credentialFields';

const logger = createLogger('loyaltyPoint');
/**
 * Loyalty Point Service for HashiCorp Vault Integration
 * 
 * This service provides a frontend interface to interact with HashiCorp Vault
 * through a backend API for loyalty point data. The backend API handles the actual Vault operations
 * while this service provides a clean interface for the React Native app.
 * 
 * All sensitive loyalty point data (account numbers, usernames, passwords, etc.) are stored securely in
 * HashiCorp Vault through the backend API.
 */

// Use the same API URL as the rest of the app
const API_BASE_URL = API_URL;

export interface LoyaltyPoint {
  id: string;
  category: string;
  loyaltyPointType: string;
  companyName: string;
  accountNumber: string;
  username: string;
  password: string;
  websiteUrl: string;
  notes: string;
}

class LoyaltyPointService {
  // Helper method to get auth headers
  private async getAuthHeaders() {
    logger.debug('=== FRONTEND LOYALTY POINT SERVICE: Getting auth token ===');
    const token = await getAuthToken();
    logger.debug('Token found:', token ? 'YES' : 'NO');
    if (!token) {
      logger.error('=== FRONTEND LOYALTY POINT SERVICE: No authentication token found ===');
      throw new Error('No authentication token found');
    }
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    logger.debug('=== FRONTEND LOYALTY POINT SERVICE: Auth headers created ===');
    return headers;
  }

  // Store loyalty point - creates DB record (simplified to match contacts pattern)
  async storeLoyaltyPoint(
    data: {
      category: string;
      loyaltyPointType: string;
      companyName: string;
      accountNumber: string;
      username: string;
      password: string;
      websiteUrl: string;
      notes?: string;
    },
  ): Promise<string> {
    logger.debug('=== FRONTEND LOYALTY POINT SERVICE: storeLoyaltyPoint called ===');
    logger.debug('API URL:', `${API_BASE_URL}/api/loyalty-points`);
    logger.debug('Loyalty point data:', {
      category: data.category,
      loyaltyPointType: data.loyaltyPointType,
      companyName: data.companyName,
      websiteUrl: data.websiteUrl,
      notes: data.notes,
    });

    try {
      logger.debug('=== FRONTEND LOYALTY POINT SERVICE: Getting auth headers ===');
      const headers = await this.getAuthHeaders();
      logger.debug('Auth headers:', { ...headers, Authorization: headers.Authorization ? 'Bearer [HIDDEN]' : 'NOT SET' });

      // Step 1: Store non-sensitive data in database
      const loyaltyPointData = {
        program: data.companyName,
        category: data.category,
        points: 0,
        notes: data.notes,
      };

      logger.debug('=== FRONTEND LOYALTY POINT SERVICE: Step 1 - Creating DB record ===');
      const dbResponse = await axios.post(`${API_BASE_URL}/api/loyalty-points`, loyaltyPointData, { headers });
      logger.debug('DB response status:', dbResponse.status);
      logger.debug('DB response data:', dbResponse.data);

      const loyaltyPointId = dbResponse.data.id;
      if (!loyaltyPointId) {
        throw new Error('No ID returned from database');
      }

      // Step 2: Store sensitive data in Vault
      logger.debug('=== FRONTEND LOYALTY POINT SERVICE: Step 2 - Storing sensitive data in Vault ===');
      const vaultData = {
        category: data.category,
        loyaltyPointType: data.loyaltyPointType,
        companyName: data.companyName,
        accountNumber: data.accountNumber,
        username: data.username,
        password: data.password,
        websiteUrl: data.websiteUrl,
        notes: data.notes,
      };

      // K5: login fields leave the device encrypted.
      const sealed = await sealCredentialFields(vaultData, LOYALTY_RECORD_FIELDS);
      const vaultResponse = await axios.post(`${API_BASE_URL}/api/vault/loyalty-points/${loyaltyPointId}`, sealed, { headers });
      logger.debug('Vault response status:', vaultResponse.status);

      if (vaultResponse.status !== 200 && vaultResponse.status !== 201) {
        throw new Error('Failed to store credentials in Vault');
      }

      logger.debug('=== FRONTEND LOYALTY POINT SERVICE: Both DB and Vault succeeded ===');
      return loyaltyPointId;
    } catch (error: any) {
      logger.error('=== FRONTEND LOYALTY POINT SERVICE: Error storing loyalty point ===', redactError(error));
      if (error.response) {
        throw new Error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
      } else if (error.request) {
        throw new Error('Network error. Please check your connection.');
      } else {
        throw error;
      }
    }
  }

  // Legacy method - kept for backward compatibility
  async storeLoyaltyPointCredentials(
    loyaltyPointId: string,
    data: {
      category: string;
      loyaltyPointType: string;
      companyName: string;
      accountNumber: string;
      username: string;
      password: string;
      websiteUrl: string;
      notes?: string;
    },
  ): Promise<void> {
    logger.debug('=== FRONTEND LOYALTY POINT SERVICE: storeLoyaltyPointCredentials called ===');
    logger.debug('Loyalty Point ID:', loyaltyPointId);

    try {
      const headers = await this.getAuthHeaders();
      const sealed = await sealCredentialFields(data, LOYALTY_RECORD_FIELDS);
      await axios.post(`${API_BASE_URL}/api/vault/loyalty-points/${loyaltyPointId}`, sealed, { headers });
      logger.debug('=== FRONTEND LOYALTY POINT SERVICE: storeLoyaltyPointCredentials succeeded ===');
    } catch (error: any) {
      logger.error('=== FRONTEND LOYALTY POINT SERVICE: Error storing loyalty point credentials ===', redactError(error));
      throw new Error('Failed to store loyalty point credentials');
    }
  }

  async getLoyaltyPointCredentials(
    loyaltyPointId: string,
    targetUserId?: string,
  ): Promise<{
    category: string;
    loyaltyPointType: string;
    companyName: string;
    accountNumber: string;
    username: string;
    password: string;
    websiteUrl: string;
    notes: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const params = targetUserId ? `?targetUserId=${encodeURIComponent(targetUserId)}` : '';
      const response = await axios.get(`${API_BASE_URL}/api/vault/loyalty-points/${loyaltyPointId}${params}`, { headers });
      // K5: decrypt with the owner's key (the response says whose it is).
      return await openCredentialFields(response.data, LOYALTY_RECORD_FIELDS, {
        ownerUserId: response.data?.ownerUserId ?? targetUserId,
      });
    } catch (error) {
      logger.error('Error retrieving loyalty point credentials from Vault:', redactError(error));
      throw new Error('Failed to retrieve loyalty point credentials');
    }
  }

  // Update loyalty point - updates DB record (simplified to match contacts pattern)
  async updateLoyaltyPoint(
    loyaltyPointId: string,
    data: {
      category: string;
      loyaltyPointType: string;
      companyName: string;
      accountNumber: string;
      username: string;
      password: string;
      websiteUrl: string;
      notes?: string;
    },
  ): Promise<LoyaltyPoint> {
    logger.debug('=== FRONTEND LOYALTY POINT SERVICE: updateLoyaltyPoint called ===');
    logger.debug('Loyalty Point ID:', loyaltyPointId);

    try {
      const headers = await this.getAuthHeaders();

      // Step 1: Update non-sensitive data in database
      const loyaltyPointData = {
        program: data.companyName,
        category: data.category,
        notes: data.notes,
      };

      logger.debug('=== FRONTEND LOYALTY POINT SERVICE: Step 1 - Updating DB record ===');
      const dbResponse = await axios.patch(`${API_BASE_URL}/api/loyalty-points/${loyaltyPointId}`, loyaltyPointData, { headers });
      logger.debug('DB update response status:', dbResponse.status);

      // Step 2: Update sensitive data in Vault
      logger.debug('=== FRONTEND LOYALTY POINT SERVICE: Step 2 - Updating Vault credentials ===');
      const vaultData = {
        category: data.category,
        loyaltyPointType: data.loyaltyPointType,
        companyName: data.companyName,
        accountNumber: data.accountNumber,
        username: data.username,
        password: data.password,
        websiteUrl: data.websiteUrl,
        notes: data.notes,
      };

      // K5: login fields leave the device encrypted.
      const sealed = await sealCredentialFields(vaultData, LOYALTY_RECORD_FIELDS);
      const vaultResponse = await axios.post(`${API_BASE_URL}/api/vault/loyalty-points/${loyaltyPointId}/update`, sealed, { headers });
      logger.debug('Vault update response status:', vaultResponse.status);

      if (vaultResponse.status !== 200 && vaultResponse.status !== 201) {
        throw new Error('Failed to update credentials in Vault');
      }

      logger.debug('=== FRONTEND LOYALTY POINT SERVICE: Both DB and Vault updated successfully ===');
      return dbResponse.data;
    } catch (error: any) {
      logger.error('=== FRONTEND LOYALTY POINT SERVICE: Error updating loyalty point ===', redactError(error));
      throw new Error('Failed to update loyalty point');
    }
  }

  // Legacy method - kept for backward compatibility
  async updateLoyaltyPointCredentials(
    loyaltyPointId: string,
    data: {
      category: string;
      loyaltyPointType: string;
      companyName: string;
      accountNumber: string;
      username: string;
      password: string;
      websiteUrl: string;
      notes?: string;
    },
  ): Promise<void> {
    logger.debug('=== FRONTEND LOYALTY POINT SERVICE: updateLoyaltyPointCredentials called ===');
    logger.debug('Loyalty Point ID:', loyaltyPointId);

    try {
      const headers = await this.getAuthHeaders();
      const sealed = await sealCredentialFields(data, LOYALTY_RECORD_FIELDS);
      await axios.post(`${API_BASE_URL}/api/vault/loyalty-points/${loyaltyPointId}/update`, sealed, { headers });
      logger.debug('=== FRONTEND LOYALTY POINT SERVICE: updateLoyaltyPointCredentials succeeded ===');
    } catch (error: any) {
      logger.error('=== FRONTEND LOYALTY POINT SERVICE: Error updating loyalty point credentials ===', redactError(error));
      throw new Error('Failed to update loyalty point credentials');
    }
  }

  async deleteLoyaltyPointCredentials(loyaltyPointId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await axios.delete(`${API_BASE_URL}/api/vault/loyalty-points/${loyaltyPointId}`, { headers });
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to delete loyalty point credentials');
      }
    } catch (error) {
      logger.error('Error deleting loyalty point credentials from Vault:', redactError(error));
      throw new Error('Failed to delete loyalty point credentials');
    }
  }

  async getAllLoyaltyPointsByCategory(category: string, targetUserId?: string): Promise<LoyaltyPoint[]> {
    try {
      const headers = await this.getAuthHeaders();
      
      // Add timeout to prevent infinite hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout (increased for AI operations)

      const params = targetUserId ? `?targetUserId=${encodeURIComponent(targetUserId)}` : '';
      const response = await axios.get(`${API_BASE_URL}/api/vault/loyalty-points/category/${category}${params}`, {
        headers,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const list = Array.isArray(response.data) ? response.data : [];
      // K5: decrypt each record with its owner's key.
      return await Promise.all(
        list.map((lp: any) => openCredentialFields(lp, LOYALTY_RECORD_FIELDS, { ownerUserId: lp?.ownerUserId })),
      );
    } catch (error: any) {
      // If it's a 404 error, the path doesn't exist yet, which is fine
      if (error.response && error.response.status === 404) {
        logger.debug('Loyalty points category path does not exist yet in vault, returning empty list');
        return [];
      }
      // Plaintext AsyncStorage fallback removed in Step 4. Empty-list result
      // on error keeps the screen renderable without writing credentials
      // off-vault. See docs/KNOWN_LIMITATIONS.md.
      logger.error('Error retrieving loyalty points by category from Vault:', redactError(error));
      return [];
    }
  }

  /** `quiet`: a count-only read never asks for the Recovery Code. */
  async getAllLoyaltyPoints(targetUserId?: string, opts: { quiet?: boolean } = {}): Promise<LoyaltyPoint[]> {
    logger.debug('=== FRONTEND LOYALTY POINT SERVICE: getAllLoyaltyPoints called ===');
    try {
      const headers = await this.getAuthHeaders();
      // K5: another person's records (successor after a verified death); the
      // server gates both calls and says whose each record is.
      const params = targetUserId ? `?targetUserId=${encodeURIComponent(targetUserId)}` : '';

      // Step 1: Get non-sensitive data from database
      logger.debug('Step 1: Fetching from database');
      const dbResponse = await axios.get(`${API_BASE_URL}/api/loyalty-points${params}`, { headers });
      logger.debug('DB response count:', dbResponse.data?.length || 0);

      const dbLoyaltyPoints = dbResponse.data || [];

      // Step 2: Get sensitive data from vault for each loyalty point
      logger.debug('Step 2: Fetching sensitive data from Vault');
      const loyaltyPointsWithVaultData = await Promise.all(
        dbLoyaltyPoints.map(async (lp: any) => {
          try {
            // Fetch vault data for this loyalty point
            const vaultResponse = await axios.get(`${API_BASE_URL}/api/vault/loyalty-points/${lp.id}${params}`, { headers });
            // K5: decrypt with the owner's key.
            const vaultData = await openCredentialFields(vaultResponse.data, LOYALTY_RECORD_FIELDS, {
              ownerUserId: vaultResponse.data?.ownerUserId ?? targetUserId,
              prompt: !opts.quiet,
            });

            return {
              id: lp.id,
              category: lp.category || 'other',
              loyaltyPointType: vaultData.loyaltyPointType || lp.category || 'other',
              companyName: lp.program,
              accountNumber: vaultData.accountNumber || '',
              username: vaultData.username || '',
              password: vaultData.password || '',
              websiteUrl: vaultData.websiteUrl || '',
              notes: lp.notes || '',
            };
          } catch (vaultError) {
            logger.warn(`Could not fetch vault data for ${lp.id}, using DB data only`);
            // If vault fetch fails, return with empty sensitive fields
            return {
              id: lp.id,
              category: lp.category || 'other',
              loyaltyPointType: lp.category || 'other',
              companyName: lp.program,
              accountNumber: '',
              username: '',
              password: '',
              websiteUrl: '',
              notes: lp.notes || '',
            };
          }
        })
      );

      logger.debug('Successfully merged', loyaltyPointsWithVaultData.length, 'loyalty points');
      return loyaltyPointsWithVaultData;
    } catch (error: any) {
      logger.error('=== FRONTEND LOYALTY POINT SERVICE: Error retrieving loyalty points ===', redactError(error));
      return [];
    }
  }

}

export const loyaltyPointService = new LoyaltyPointService();